import { FileWriter } from '@/platform'
import { BrowserFileUtils } from '@/platform/utils/BrowserFileUtils'
import type MainProject from '@/model/MainProject'
import type DataFileMap from '@/model/Resource/DataFileMap'
import type {
  PublishArtifactFile,
  PublishArtifacts,
  PublishManifestEntry,
  PublishResourceRoots,
} from '@/publish/types'
import {
  resolveFontDisplayNameFromStoredPath,
} from '@/features/project-font/runtime'
import { normalizeProjectAudioEventPaths } from '@/features/virtual-resource/audioEventPaths'
import { writeMallNewBin } from '@/features/project-mall/runtime'
import { PROJECT_CURRENT_PASSTHROUGH_MAP } from '@/features/project-cloud/ossPaths'
import BrowserUtil from '@/platform/oss/browserUtil'
import { countProjectWords } from '@/features/project-stats/wordCount'
import { sanitizeLogValue, sanitizeSignedUrl } from '@/utils/sanitizeLog'

const BITMAP_FONT_URL_PREFIX = 'cgbitmap://'
const MD5_HEX_RE = /^[a-f0-9]{32}$/i
const REGENERATED_OR_INTERNAL_RUNTIME_PATHS = [
  'data/game.bin',
  'data/var.bin',
  'data/mallnew.bin',
  'data/projects.bin',
  'data/map.bin',
] as const

type PublishArtifactsBuildOptions = {
  onLog?: (message: string, payload?: Record<string, unknown>) => void
  networkPolicy?: 'full' | 'avoid-remote'
  publishVersion?: string
  skipWordCount?: boolean
}

function logBuildArtifact(
  options: PublishArtifactsBuildOptions | undefined,
  message: string,
  payload?: Record<string, unknown>,
): void {
  options?.onLog?.(message, payload ? sanitizeLogValue(payload) : undefined)
}

function normalizePath(value: string): string {
  return String(value || '')
    .replace(/\\/g, '/')
    .replace(/^\/+/, '')
    .replace(/\/+/g, '/')
    .trim()
}

function joinSegments(...segments: Array<string | null | undefined>): string {
  return segments
    .map((item) => normalizePath(item ?? ''))
    .filter(Boolean)
    .join('/')
}

function bytesToFile(bytes: Uint8Array, fileName: string): File {
  const buffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer
  return new File([buffer], fileName, { type: 'application/octet-stream' })
}

function makeArtifact(relativePath: string, uploadPath: string, bytes: Uint8Array): PublishArtifactFile {
  return {
    relativePath,
    uploadPath,
    bytes,
    size: bytes.byteLength,
    md5: BrowserFileUtils.getMd5HashFromUint8Array(bytes),
  }
}

function toManifestEntry(file: Pick<PublishArtifactFile, 'relativePath' | 'size' | 'md5'>): PublishManifestEntry {
  return {
    relativePath: file.relativePath,
    size: file.size,
    md5: file.md5,
  }
}

function writeMapManifest(files: PublishManifestEntry[]): Uint8Array {
  const writer = new FileWriter()
  writer.writeInt(files.length)
  for (const file of files) {
    writer.writeString(file.relativePath)
    writer.writeInt(file.size)
    writer.writeString(file.md5)
  }
  return writer.toUint8Array()
}

function serializeWithWriter(callback: (writer: FileWriter) => void): Uint8Array {
  const writer = new FileWriter()
  callback(writer)
  return writer.toUint8Array()
}

type ResourceLeafMeta = {
  name: string
  relativePath: string
  mappingPath: string
  resourceUrl: string
  md5: string
  size: number
}

const resourceLeafCache = new WeakMap<DataFileMap, ResourceLeafMeta[]>()

function collectResourceLeaves(root: DataFileMap | null | undefined): ResourceLeafMeta[] {
  if (!root) return []
  const cached = resourceLeafCache.get(root)
  if (cached) return cached
  const out: ResourceLeafMeta[] = []
  const stack: DataFileMap[] = [root]
  while (stack.length) {
    const node = stack.pop()!
    if (node.isFolder) {
      for (let index = node.children.length - 1; index >= 0; index -= 1) {
        stack.push(node.children[index]!)
      }
      continue
    }
    out.push({
      name: node.name,
      relativePath: node.relativePath,
      mappingPath: node.mappingPath,
      resourceUrl: node.resourceUrl,
      md5: node.resourceMd5,
      size: node.resourceSize,
    })
  }
  resourceLeafCache.set(root, out)
  return out
}

function resolveStoredPath(meta: ResourceLeafMeta, fallbackMappingPath: string): string {
  const mappingPath = normalizePath(meta.mappingPath || fallbackMappingPath)
  const filePath = normalizePath(meta.relativePath || meta.name)
  return joinSegments(mappingPath, filePath) || filePath
}

function tryExtractMd5FromUrl(resourceUrl: string): string {
  try {
    const url = new URL(resourceUrl, window.location.href)
    const lastSegment = url.pathname.split('/').filter(Boolean).pop() ?? ''
    return MD5_HEX_RE.test(lastSegment) ? lastSegment.toLowerCase() : ''
  } catch {
    return ''
  }
}

function tryResolveOssObjectKeyFromUrl(resourceUrl: string): string {
  try {
    const parsed = new URL(resourceUrl, window.location.href)
    if (!/\.aliyuncs\.com$/i.test(parsed.hostname)) return ''
    const decodedPath = decodeURIComponent(parsed.pathname).replace(/^\/+/, '')
    const rootDir = BrowserUtil.getInstance().getRootDir().replace(/^\/+|\/+$/g, '')
    if (rootDir && decodedPath.toLowerCase().startsWith(`${rootDir.toLowerCase()}/`)) {
      return decodedPath.slice(rootDir.length + 1)
    }
    return decodedPath
  } catch {
    return ''
  }
}

async function fetchBytes(resourceUrl: string, options?: PublishArtifactsBuildOptions): Promise<Uint8Array> {
  logBuildArtifact(options, 'fetch-bytes:start', { resourceUrl })
  const ossObjectKey = tryResolveOssObjectKeyFromUrl(resourceUrl)
  if (ossObjectKey && BrowserUtil.getInstance().isConfigured()) {
    const bytes = new Uint8Array(await BrowserFileUtils.blobToArrayBuffer(await BrowserUtil.getInstance().getBlob(ossObjectKey)))
    logBuildArtifact(options, 'fetch-bytes:done', { resourceUrl, size: bytes.byteLength, source: 'oss-private' })
    return bytes
  }
  const response = await fetch(resourceUrl)
  if (!response.ok) {
    logBuildArtifact(options, 'fetch-bytes:error', { resourceUrl, status: response.status })
    throw new Error(`资源读取失败：HTTP ${response.status} · ${sanitizeSignedUrl(resourceUrl)}`)
  }
  const bytes = await BrowserFileUtils.responseToUint8Array(response)
  logBuildArtifact(options, 'fetch-bytes:done', { resourceUrl, size: bytes.byteLength })
  return bytes
}

async function createManifestEntryFromUrl(
  relativePath: string,
  resourceUrl: string,
  cachedMeta: Pick<ResourceLeafMeta, 'md5' | 'size'>,
  options?: PublishArtifactsBuildOptions,
): Promise<PublishManifestEntry> {
  const cachedMd5 = cachedMeta.md5.trim().toLowerCase()
  const cachedSize = Math.floor(cachedMeta.size)
  const md5FromUrl = tryExtractMd5FromUrl(resourceUrl)
  const md5 = MD5_HEX_RE.test(cachedMd5) ? cachedMd5 : md5FromUrl
  if (md5 && cachedSize > 0) {
    logBuildArtifact(options, 'manifest-entry:cached', {
      relativePath,
      resourceUrl,
      md5,
      size: cachedSize,
    })
    return {
      relativePath,
      size: cachedSize,
      md5,
    }
  }
  if (options?.networkPolicy === 'avoid-remote') {
    const syntheticMd5 = md5 || BrowserFileUtils.getMd5HashFromString(relativePath)
    logBuildArtifact(options, 'manifest-entry:synthetic', {
      relativePath,
      resourceUrl,
      md5: syntheticMd5,
    })
    return {
      relativePath,
      size: 1,
      md5: syntheticMd5,
    }
  }
  if (md5) {
    logBuildArtifact(options, 'manifest-entry:md5-direct', {
      relativePath,
      resourceUrl,
      md5,
    })
    return {
      relativePath,
      size: 1,
      md5,
    }
  }

  logBuildArtifact(options, 'manifest-entry:fetch-fallback', { relativePath, resourceUrl })
  const bytes = await fetchBytes(resourceUrl, options)
  return {
    relativePath,
    size: bytes.byteLength,
    md5: BrowserFileUtils.getMd5HashFromUint8Array(bytes),
  }
}

async function buildFontArtifacts(
  project: MainProject,
  options?: PublishArtifactsBuildOptions,
): Promise<PublishArtifactFile[]> {
  const out: PublishArtifactFile[] = []
  const seen = new Set<string>()

  logBuildArtifact(options, 'font-artifacts:start', {
    bitmapFontFiles: project.bitmapFont?.files.length ?? 0,
  })

  for (const file of project.bitmapFont?.files ?? []) {
    const relativePath = joinSegments('Font', file.fileName)
    const key = relativePath.toLowerCase()
    if (!relativePath || seen.has(key)) continue
    seen.add(key)
    logBuildArtifact(options, 'font-artifact:bitmap', { relativePath, size: file.bytes.byteLength })
    out.push(makeArtifact(relativePath, joinSegments('other', file.fileName), file.bytes))
  }

  logBuildArtifact(options, 'font-artifacts:done', { count: out.length })
  return out
}

async function buildFontManifestEntries(
  fontRoot: DataFileMap | null,
  bitmapFontPaths: ReadonlySet<string>,
  options?: PublishArtifactsBuildOptions,
): Promise<PublishManifestEntry[]> {
  const entries: PublishManifestEntry[] = []
  const seen = new Set<string>()
  for (const leaf of collectResourceLeaves(fontRoot)) {
    if (!leaf.resourceUrl || leaf.resourceUrl.startsWith(BITMAP_FONT_URL_PREFIX)) continue
    const relativePath = resolveStoredPath(leaf, 'Font')
    const key = relativePath.toLowerCase()
    if (!relativePath || seen.has(key) || bitmapFontPaths.has(key)) continue
    seen.add(key)
    entries.push(await createManifestEntryFromUrl(relativePath, leaf.resourceUrl, leaf, options))
  }
  logBuildArtifact(options, 'font-manifest:done', { count: entries.length })
  return entries
}

async function buildResourceManifestEntries(
  roots: Pick<PublishResourceRoots, 'graphic' | 'audio' | 'video'>,
  options?: PublishArtifactsBuildOptions,
): Promise<PublishManifestEntry[]> {
  const entries: PublishManifestEntry[] = []
  const seen = new Set<string>()
  const groups: Array<{ root: DataFileMap | null; fallback: string }> = [
    { root: roots.graphic, fallback: 'Graphics' },
    { root: roots.audio, fallback: 'Audio' },
    { root: roots.video, fallback: 'Graphics/video' },
  ]

  for (const group of groups) {
    for (const leaf of collectResourceLeaves(group.root)) {
      if (!leaf.resourceUrl) continue
      const relativePath = resolveStoredPath(leaf, group.fallback)
      const key = relativePath.toLowerCase()
      if (!relativePath || seen.has(key)) continue
      seen.add(key)
      entries.push(await createManifestEntryFromUrl(relativePath, leaf.resourceUrl, leaf, options))
    }
  }

  logBuildArtifact(options, 'resource-manifest:done', { count: entries.length })
  return entries
}

function parsePassthroughManifestEntry(value: unknown): PublishManifestEntry | null {
  if (!value || typeof value !== 'object') return null
  const record = value as Record<string, unknown>
  const relativePath = normalizePath(String(record.relativePath ?? ''))
  const size = Number(record.size ?? 0)
  const md5 = String(record.md5 ?? '').trim().toLowerCase()
  if (!relativePath || !Number.isFinite(size) || size <= 0 || !MD5_HEX_RE.test(md5)) return null
  return { relativePath, size, md5 }
}

async function buildOssPassthroughManifestEntries(
  project: MainProject,
  existingPaths: Set<string>,
  options?: PublishArtifactsBuildOptions,
): Promise<PublishManifestEntry[]> {
  const guid = normalizePath(project.projectHeader.ID)
  const oss = BrowserUtil.getInstance()
  if (!guid || !oss.isConfigured() || !oss.getRootDir()) return []

  const objectKey = joinSegments(guid, PROJECT_CURRENT_PASSTHROUGH_MAP)
  let raw = ''
  try {
    raw = await oss.getText(objectKey)
  } catch (error) {
    logBuildArtifact(options, 'passthrough-map:skip', {
      objectKey,
      error: error instanceof Error ? error.message : String(error),
    })
    return []
  }

  let parsed: unknown
  try {
    parsed = JSON.parse(raw) as unknown
  } catch (error) {
    logBuildArtifact(options, 'passthrough-map:invalid', {
      objectKey,
      error: error instanceof Error ? error.message : String(error),
    })
    return []
  }
  const list = Array.isArray(parsed) ? parsed : []
  const out: PublishManifestEntry[] = []
  for (const item of list) {
    const entry = parsePassthroughManifestEntry(item)
    if (!entry) continue
    const key = entry.relativePath.toLowerCase()
    if (existingPaths.has(key)) continue
    existingPaths.add(key)
    out.push(entry)
  }
  logBuildArtifact(options, 'passthrough-map:done', { count: out.length })
  return out
}

function createRuntimePathSet(files: PublishArtifactFile[]): Set<string> {
  const out = new Set(files.map((file) => file.relativePath.toLowerCase()))
  for (const path of REGENERATED_OR_INTERNAL_RUNTIME_PATHS) {
    out.add(path)
  }
  return out
}

export function createUploadFile(artifact: PublishArtifactFile): File {
  return bytesToFile(artifact.bytes, artifact.relativePath.split('/').pop() || 'data.bin')
}

export async function buildPublishArtifacts(
  project: MainProject,
  resourceRoots: PublishResourceRoots,
  options?: PublishArtifactsBuildOptions,
): Promise<PublishArtifacts> {
  normalizeProjectAudioEventPaths(project, resourceRoots.audio)
  logBuildArtifact(options, 'build-artifacts:start', {
    projectId: project.projectHeader.ID,
    width: project.screenHash.width,
    height: project.screenHash.height,
  })
  const publishVersion = normalizePath(options?.publishVersion || '0') || '0'
  const editorProjectBin = makeArtifact(
    'Data/ProjectS.bin',
    `other/ProjectS${publishVersion}.bin`,
    project.writeBinBuffer(),
  )
  const runtimeFontName = resolveFontDisplayNameFromStoredPath(
    resourceRoots.font,
    project.systemData.totalSetting.FontName,
  )

  const runtimeFiles: PublishArtifactFile[] = []
  runtimeFiles.push(
    makeArtifact(
      'Data/Game.bin',
      'bin/Game.bin',
      project.writeGameBinBuffer(
        project.screenHash.width,
        project.screenHash.height,
        project.projectHeader.ID,
        true,
        runtimeFontName,
      ),
    ),
  )

  runtimeFiles.push(
    makeArtifact(
      'Data/Var.bin',
      'bin/Var.bin',
      serializeWithWriter((writer) => {
        project.gameVar.writeVersionedOui(writer)
      }),
    ),
  )

  runtimeFiles.push(
    makeArtifact(
      'Data/MallNew.bin',
      'bin/MallNew.bin',
      writeMallNewBin(project),
    ),
  )

  const fontFiles = await buildFontArtifacts(project, options)
  const bitmapFontPaths = new Set(
    (project.bitmapFont?.files ?? []).map((file) => joinSegments('Font', file.fileName).toLowerCase()),
  )
  const fontEntries = await buildFontManifestEntries(resourceRoots.font, bitmapFontPaths, options)
  const resourceEntries = await buildResourceManifestEntries(resourceRoots, options)
  const mapEntriesWithoutPassthrough: PublishManifestEntry[] = [
    ...runtimeFiles.map(toManifestEntry),
    ...fontEntries,
    ...resourceEntries,
    ...fontFiles.map(toManifestEntry),
  ]
  const existingMapPaths = new Set([
    ...createRuntimePathSet(runtimeFiles),
    ...fontFiles.map((file) => file.relativePath.toLowerCase()),
    ...fontEntries.map((entry) => entry.relativePath.toLowerCase()),
    ...resourceEntries.map((entry) => entry.relativePath.toLowerCase()),
  ])
  const passthroughEntries = await buildOssPassthroughManifestEntries(project, existingMapPaths, options)
  const mapEntries: PublishManifestEntry[] = [
    ...mapEntriesWithoutPassthrough,
    ...passthroughEntries,
  ]
  const mapBin = makeArtifact(
    'Data/Map.bin',
    'map.bin',
    writeMapManifest(mapEntries),
  )

  const publishContentSize = mapEntries.reduce(
    (sum, item) => sum + item.size,
    0,
  )
  const totalWordCount = options?.skipWordCount ? 0 : countProjectWords(project)

  logBuildArtifact(options, 'build-artifacts:done', {
    runtimeFiles: runtimeFiles.length,
    fontFiles: fontFiles.length,
    mapEntries: mapEntries.length,
    publishContentSize,
    totalWordCount,
  })
  return {
    editorProjectBin,
    runtimeFiles,
    fontFiles,
    mapEntries,
    mapBin,
    publishContentSize,
    totalWordCount,
  }
}
